/*
 * Cart remove confirmation.
 *
 * Two behaviours, for both the cart page and the cart drawer:
 *  1. Pressing minus while the quantity is already at its minimum asks for
 *     confirmation instead of doing nothing (the button is otherwise inert,
 *     so people repeatedly press it expecting the item to go).
 *  2. The trash button in each line item routes through the same prompt.
 *
 * Confirming clicks the line item's existing <m-cart-remove-button>, so the
 * removal keeps using the theme's own cart update path.
 */
(function () {
  var DIALOG_ID = 'm-cart-remove-confirm';

  function lineItemOf(el) {
    return el.closest('[data-index]') || el.closest('.m-cart-item') || el.closest('.m-cart-drawer-item');
  }

  function removeButtonFor(el) {
    var row = lineItemOf(el);
    if (!row) return null;
    // The row itself may be the element carrying data-index; search within, then near.
    return row.querySelector('m-cart-remove-button') ||
      (row.parentElement && row.parentElement.querySelector('m-cart-remove-button'));
  }

  /*
   * Only ever read the dedicated title element. The image is wrapped in its own
   * product link, and `responsive-image` puts a <noscript><img ...></noscript>
   * fallback inside it — with JS enabled the browser parses that as raw TEXT, so
   * a looser selector (e.g. a[href*="/products/"]) returns the literal <img ...>
   * markup as the "title". Anything still containing a tag is discarded.
   */
  function titleFor(el) {
    var row = lineItemOf(el);
    if (!row) return '';
    var t = row.querySelector('[data-role="product-title"], .m-cart-item__title');
    if (!t) return '';
    var clone = t.cloneNode(true);
    Array.prototype.forEach.call(clone.querySelectorAll('noscript, script, style'), function (n) {
      n.parentNode.removeChild(n);
    });
    var text = (clone.textContent || '').replace(/\s+/g, ' ').trim();
    if (text.indexOf('<') !== -1) return '';
    return text.length > 120 ? text.slice(0, 117) + '…' : text;
  }

  /*
   * The line item's thumbnail, so people can see what they are removing.
   * Both the cart page and the drawer wrap it in .m-cart-item__image. The
   * <noscript> fallback inside `responsive-image` is parsed as text while JS
   * is on, so querySelector only ever reaches the real <img>.
   */
  function imageFor(el) {
    var row = lineItemOf(el);
    if (!row) return null;
    var img = row.querySelector('.m-cart-item__image img');
    if (!img) return null;
    var src = img.currentSrc || img.getAttribute('src');
    if (!src) return null;
    return { src: src, srcset: img.getAttribute('srcset') || '', alt: img.getAttribute('alt') || '' };
  }

  function atMinimum(input) {
    if (!input) return false;
    var min = parseInt(input.min, 10);
    if (isNaN(min)) min = 1;
    return parseInt(input.value, 10) <= min;
  }

  function buildDialog() {
    var existing = document.getElementById(DIALOG_ID);
    if (existing) return existing;

    var wrap = document.createElement('div');
    wrap.id = DIALOG_ID;
    wrap.className = 'm-cart-confirm';
    wrap.setAttribute('hidden', '');
    wrap.innerHTML =
      '<div class="m-cart-confirm__backdrop" data-confirm-dismiss></div>' +
      '<div class="m-cart-confirm__box" role="dialog" aria-modal="true" aria-labelledby="' + DIALOG_ID + '-title">' +
      '  <p class="m-cart-confirm__title" id="' + DIALOG_ID + '-title">Remove this product from the cart?</p>' +
      '  <div class="m-cart-confirm__product" data-confirm-product hidden>' +
      '    <img class="m-cart-confirm__image" data-confirm-image alt="" width="56" height="56">' +
      '    <p class="m-cart-confirm__item" data-confirm-item></p>' +
      '  </div>' +
      '  <div class="m-cart-confirm__actions">' +
      '    <button type="button" class="m-cart-confirm__btn m-cart-confirm__btn--no" data-confirm-dismiss>No, keep it</button>' +
      '    <button type="button" class="m-cart-confirm__btn m-cart-confirm__btn--yes" data-confirm-accept>Yes, remove</button>' +
      '  </div>' +
      '</div>';
    document.body.appendChild(wrap);

    wrap.addEventListener('click', function (e) {
      if (e.target.closest('[data-confirm-dismiss]')) close();
      if (e.target.closest('[data-confirm-accept]')) accept();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !wrap.hasAttribute('hidden')) close();
    });
    return wrap;
  }

  var pendingRemoveButton = null;

  function open(removeButton, name, image) {
    var dialog = buildDialog();
    pendingRemoveButton = removeButton;
    dialog.querySelector('[data-confirm-item]').textContent = name || '';

    var product = dialog.querySelector('[data-confirm-product]');
    var img = dialog.querySelector('[data-confirm-image]');
    if (image) {
      img.src = image.src;
      if (image.srcset) img.srcset = image.srcset;
      else img.removeAttribute('srcset');
      img.sizes = '56px';
      img.alt = image.alt;
      img.hidden = false;
    } else {
      img.removeAttribute('src');
      img.removeAttribute('srcset');
      img.hidden = true;
    }
    // Nothing to show at all: keep the dialog to just its question.
    product.hidden = !image && !name;

    dialog.removeAttribute('hidden');
    // next frame so the transition runs
    requestAnimationFrame(function () { dialog.classList.add('is-open'); });
    // Focus the safe action: pressing Enter on an unread dialog should keep
    // the product, not remove it.
    var keep = dialog.querySelector('[data-confirm-dismiss].m-cart-confirm__btn');
    if (keep) keep.focus();
  }

  function close() {
    var dialog = document.getElementById(DIALOG_ID);
    if (!dialog) return;
    dialog.classList.remove('is-open');
    pendingRemoveButton = null;
    setTimeout(function () { dialog.setAttribute('hidden', ''); }, 200);
  }

  function accept() {
    var btn = pendingRemoveButton;
    close();
    if (btn) btn.click();
  }

  // Capture phase: run before <m-quantity-input>'s own click handler.
  document.addEventListener('click', function (e) {
    var minus = e.target.closest('.m-quantity__button[name="minus"]');
    if (minus) {
      var quantityEl = minus.closest('m-quantity-input') || minus.closest('.m-quantity');
      var input = quantityEl && quantityEl.querySelector('input');
      if (!input || !atMinimum(input)) return;
      if (!minus.closest('m-cart, m-cart-drawer-items, .m-cart-drawer, .m-cart')) return;
      var removeButton = removeButtonFor(minus);
      if (!removeButton) return;
      e.preventDefault();
      e.stopPropagation();
      open(removeButton, titleFor(minus), imageFor(minus));
      return;
    }

    var trash = e.target.closest('[data-cart-remove-trash]');
    if (trash) {
      e.preventDefault();
      e.stopPropagation();
      var target = removeButtonFor(trash);
      if (target) open(target, titleFor(trash), imageFor(trash));
    }
  }, true);
})();
